import * as Yup from "yup";
import { Transitions } from "../finite-state-machine";
import { BarcodeReaderInfo, ReaderData } from "./types";

export const BarcodeReaders: Record<string, BarcodeReaderInfo> = {
  code_128_reader: {
    value: "code_128_reader",
    format: "code_128",
    label: "Code 128",
  },
  ean_reader: {
    value: "ean_reader",
    format: "ean_13",
    label: "EAN",
  },
  ean_5_reader: {
    value: "ean_5_reader",
    format: "ean_5",
    label: "EAN-5",
    supplementOf: "ean_reader",
  },
  ean_2_reader: {
    value: "ean_2_reader",
    format: "ean_2",
    label: "EAN-2",
    supplementOf: "ean_reader",
  },
  ean_8_reader: { value: "ean_8_reader", format: "ean_8", label: "EAN-8" },
  code_39_reader: {
    value: "code_39_reader",
    format: "code_39",
    label: "Code 39",
  },
  code_39_vin_reader: {
    value: "code_39_vin_reader",
    format: "code_39_vin",
    label: "Code 39 VIN",
  },
  codabar_reader: {
    value: "codabar_reader",
    format: "codabar",
    label: "Codabar",
  },
  upc_reader: { value: "upc_reader", format: "upc_a", label: "UPC" },
  upc_e_reader: { value: "upc_e_reader", format: "upc_e", label: "UPC-E" },
  i2of5_reader: {
    value: "i2of5_reader",
    format: "i2of5",
    label: "Interleaved Two of Five",
  },
  ["2of5_reader"]: {
    value: "2of5_reader",
    format: "2of5",
    label: "Two of Five",
  },
  code_93_reader: {
    value: "code_93_reader",
    format: "code_93",
    label: "Code 93",
  },
  code_32_reader: {
    value: "code_32_reader",
    format: "code_32",
    label: "Code 32",
  },
  qrcode_reader: {
    value: "qrcode_reader",
    format: "qr_code",
    label: "QR Code",
  },
} as const;

export const HORIZONTAL_PADDING_FACTOR = 0.05;
export const DEFAULT_ASPECT_RATIO = 1.6;
export const DEFAULT_MAX_WIDTH = 300;

export const DIMMED_COLOR = "rgba(0,0,0,0.5)";
export const BRACKET_SIZE = 50;
export const BRACKET_THICKNESS = 4;

export const barcodeSettingsStorageKey = "__BARCODE_SETTINGS__";

export const RE_SCAN_DELAY = 1000;

export const defaultReaders: ReaderData = {
  upc_reader: {
    position: 0,
    value: "upc_reader",
    enabled: true,
  },
  ean_reader: {
    position: 1,
    value: "ean_reader",
    enabled: true,
  },
  ean_5_reader: {
    position: 2,
    value: "ean_5_reader",
    enabled: true,
  },
  ean_2_reader: {
    position: 3,
    value: "ean_2_reader",
    enabled: true,
  },
  ean_8_reader: {
    position: 4,
    value: "ean_8_reader",
    enabled: true,
  },
  i2of5_reader: {
    position: 5,
    value: "i2of5_reader",
    enabled: true,
  },
  code_128_reader: {
    position: 6,
    value: "code_128_reader",
    enabled: true,
  },
  code_39_reader: {
    position: 7,
    value: "code_39_reader",
    enabled: true,
  },
} as ReaderData;

const scannerSettingsBaseItemSchema = Yup.object({
  position: Yup.number(),
  enabled: Yup.boolean(),
}).required();

export const QuaggaScannerSettingsItemSchema = scannerSettingsBaseItemSchema
  .shape({
    value: Yup.string(),
  })
  .required();

export const yupObjectOf = <T extends Yup.AnySchema>(schema: T) => ({
  name: "objectOf",
  exclusive: false,
  message: "Object values don't match the given schema",
  test: (value: null | Record<string, unknown>) => {
    return (
      value === null ||
      Object.values(value).every(() => schema.isValidSync(value))
    );
  },
});

export const QuaggaScannerSettingsItemsSchema = yupObjectOf(
  QuaggaScannerSettingsItemSchema
);

export const defaultConstraints = {
  width: 640,
  height: 480,
};

export const locatorConfig = {
  debug: {
    showCanvas: true,
    showPatches: true,
    showFoundPatches: true,
    showSkeleton: true,
    showLabels: true,
    showPatchLabels: true,
    showRemainingPatchLabels: true,
    boxFromPatches: {
      showTransformed: true,
      showTransformedBox: true,
      showBB: true,
    },
  },
};

export const debugConfig = {
  drawBoundingBox: true,
  showFrequency: true,
  drawScanline: true,
  showPattern: true,
};

export enum ScannerState {
  STOPPED,
  RUNNING,
  PAUSED,
}

export const scannerStateTransitions: Transitions<ScannerState> = {
  [ScannerState.STOPPED]: {
    to: [ScannerState.RUNNING],
  },
  [ScannerState.RUNNING]: {
    to: [ScannerState.PAUSED, ScannerState.STOPPED],
  },
  [ScannerState.PAUSED]: { to: [ScannerState.RUNNING, ScannerState.STOPPED] },
};
